import { type ReactNode } from "react";

import { Disclosure, DisclosureHeader, DisclosurePanel } from "#components/Disclosure";
import { cn } from "#utils";

import { SidebarLink } from "./SidebarLink";

function matchesNavItem(currentRoute: string, navItem: NavItem): boolean {
  if (navItem.target) {
    return (
      currentRoute.startsWith(navItem.target) ||
      (navItem.activePaths?.some((path) => currentRoute.startsWith(path)) ?? false)
    );
  }
  return containsCurrentRoute(currentRoute, navItem.children ?? []);
}

function containsCurrentRoute(currentRoute: string, navItems: NavItem[]): boolean {
  return navItems.some((navItem) => matchesNavItem(currentRoute, navItem));
}

/**
 * Visual style of the sidebar navigation.
 *
 * - `default`: sections render as prominent rows with an optional section
 *   icon; children are indented, text-only links.
 * - `grouped`: sections render as small uppercase-style group labels;
 *   children sit flush with the label and carry their own icons.
 */
export type SidebarVariant = "default" | "grouped";

interface BaseNavItems {
  label: string;
  id: string;
  isExternalLink?: boolean;
  mainPath?: string;
  permissionRequired?: {
    object: string;
    action: string;
  };
  Icon?: ({ className }: { className?: string }) => ReactNode;
  /** Optional chip text displayed right-aligned next to the label */
  chip?: string;
  /** Additional route prefixes that should activate this nav item */
  activePaths?: string[];
  /**
   * For sections: whether this section can be collapsed, overriding the
   * Sidebar-level `collapsible` default for this item only.
   */
  collapsible?: boolean;
  /**
   * For collapsible sections, the preferred initial expanded state. The section
   * still opens on mount whenever the active route is one of its children, even
   * if this is false.
   */
  defaultExpanded?: boolean;
  /** For collapsible sections, called whenever the section is expanded or collapsed — e.g. to persist the state. */
  onExpandedChange?: (isExpanded: boolean) => void;
  /** Render a gap above this item, starting a new visual group. Ignored for the first item. */
  startsGroup?: boolean;
  /**
   * Called once the user has hovered or focused this item long enough to look
   * like they mean to follow it, for callers that want to start loading the
   * linked page's data before the click.
   */
  onPreload?: () => void;
}

interface NavItemsLeafNode extends BaseNavItems {
  target: string;
  children?: never;
  popoutContent?: never;
}

interface NavItemsBranchNode extends BaseNavItems {
  children: NavItem[];
  target?: never;
  popoutContent?: never;
}

interface NavItemsPopoutNode extends BaseNavItems {
  popoutContent: NonNullable<ReactNode>;
  target?: never;
  children?: never;
}

export type NavItem = NavItemsBranchNode | NavItemsLeafNode | NavItemsPopoutNode;

interface NavItemRowProps {
  label: string;
  hasChildren?: boolean;
  collapsed?: boolean;
  Icon?: ({ className }: { className?: string }) => ReactNode;
}
function NavItemParentRow({ label, collapsed, Icon }: NavItemRowProps) {
  return (
    <div className={cn("flex w-full items-center", Icon ? "gap-xs" : "")}>
      {Icon ? (
        <div
          className={cn(
            "flex items-center justify-center stroke-text-primary",
            collapsed ? "w-full" : "",
          )}
        >
          <Icon className={cn("stroke-0", collapsed ? "size-lg" : "size-md")} />
        </div>
      ) : null}

      {!collapsed ? (
        <div className="whitespace-nowrap text-label-small font-normal text-text-secondary">
          {label}
        </div>
      ) : null}
    </div>
  );
}

interface Props {
  data: NavItem;
  pathname: string;
  collapsed?: boolean;
  /** Whether sections can be collapsed. When false (default), sections are always expanded with no toggle trigger. Overridable per section via `NavItem.collapsible`. */
  collapsible?: boolean;
  /** Visual style of the item, see {@link SidebarVariant}. */
  variant?: SidebarVariant;
  navigate: (to: string) => void;
  Icon?: ({ className }: { className?: string }) => ReactNode;
  onPopoutToggle?: (id: string) => void;
}

export function NavItemList({
  data,
  collapsed = false,
  collapsible = false,
  variant = "default",
  pathname,
  navigate,
  Icon,
  onPopoutToggle,
}: Props) {
  if (data.popoutContent !== undefined) {
    const isActive = !!data.mainPath && pathname.startsWith(data.mainPath);
    return (
      <button
        className={cn(
          "flex w-full cursor-pointer items-center border-r-2 py-xxs pl-xl pr-[20px] transition-colors duration-300",
          isActive
            ? "border-outline-brand bg-overlay-brand font-semibold text-text-brand"
            : "border-transparent text-text-secondary hover:text-text-primary",
        )}
        onClick={() => onPopoutToggle?.(data.id)}
        type="button"
      >
        {Icon ? (
          <div className="mr-xs flex items-center justify-center stroke-text-primary">
            <Icon className={cn("stroke-0", collapsed ? "size-lg" : "size-md")} />
          </div>
        ) : null}
        {!collapsed ? (
          <span
            className={cn(
              "whitespace-nowrap text-label-small",
              isActive ? "font-semibold" : "font-medium",
            )}
          >
            {data.label}
          </span>
        ) : null}
        {!collapsed && data.chip ? (
          <span className="ml-auto rounded-sm bg-background-informative px-xs py-3xs text-label-extra-small font-bold capitalize text-text-informative">
            {data.chip}
          </span>
        ) : null}
      </button>
    );
  }

  if (data.children) {
    const isGrouped = variant === "grouped";
    const sectionCollapsible = data.collapsible ?? collapsible;
    const hasActiveChild = containsCurrentRoute(pathname, data.children);
    const childLinks = data.children.map((navitem: NavItem) => (
      <SidebarLink
        activePaths={navitem.activePaths}
        indented={!isGrouped && !collapsed}
        isExternalLink={navitem.isExternalLink}
        key={navitem.id}
        navigate={navigate}
        onPreload={navitem.onPreload}
        pathname={pathname}
        target={navitem.target ?? ""}
        variant={variant}
        isChild
      >
        <div className="flex items-center gap-xs">
          {/* In the default variant section children are text-only when expanded
              (in collapsed mode the icon is all that is shown, so keep it there
              to avoid blank rows); the grouped variant always shows the icon. */}
          {(isGrouped || collapsed) && navitem.Icon ? (
            <navitem.Icon className="size-md stroke-0 shrink-0" />
          ) : null}
          {!collapsed ? (
            <span className={isGrouped ? "whitespace-nowrap" : undefined}>{navitem.label}</span>
          ) : null}
          {!collapsed && navitem.chip ? (
            <span className="rounded-xl bg-data-viz-soft-indigo px-xs text-label-extra-small font-bold capitalize text-data-viz-indigo-800">
              {navitem.chip}
            </span>
          ) : null}
        </div>
      </SidebarLink>
    ));

    // In the collapsed rail the grouped variant has nothing to show for a
    // section header (no icon), so the children render on their own.
    if (isGrouped && collapsed) {
      return <div>{childLinks}</div>;
    }

    const sectionLabel = isGrouped ? (
      <div className="whitespace-nowrap text-label-extra-small-strong capitalize text-text-secondary">
        {data.label}
      </div>
    ) : (
      <NavItemParentRow Icon={Icon} collapsed={collapsed} label={data.label} />
    );
    const headerPadding = isGrouped ? "py-3xs" : "py-xxs";

    if (!sectionCollapsible) {
      return (
        <div>
          <div className={cn("flex w-full items-center pl-xl pr-[20px]", headerPadding)}>
            {sectionLabel}
          </div>
          <div>{childLinks}</div>
        </div>
      );
    }

    return (
      <Disclosure
        // Open on mount when the active route is inside this section, even if the
        // persisted/default preference is collapsed (deep link / refresh). Otherwise
        // honor the preference. Route-based opening does not overwrite the preference.
        defaultExpanded={hasActiveChild || (data.defaultExpanded ?? false)}
        onExpandedChange={data.onExpandedChange}
      >
        <DisclosureHeader
          className={cn("flex w-full items-center justify-between pl-xl pr-[20px]", headerPadding)}
          collapsed={collapsed}
          iconClassName={isGrouped ? "size-sm text-text-secondary" : undefined}
        >
          {sectionLabel}
        </DisclosureHeader>
        <DisclosurePanel>{childLinks}</DisclosurePanel>
      </Disclosure>
    );
  }

  return (
    <SidebarLink
      activePaths={data.activePaths}
      isExternalLink={data.isExternalLink}
      navigate={navigate}
      onPreload={data.onPreload}
      pathname={pathname}
      target={data.target}
      variant={variant}
      isChild
    >
      <div className="flex items-center gap-xs">
        {Icon ? <Icon className="size-md shrink-0 stroke-0" /> : null}
        {!collapsed ? (
          <span className={variant === "grouped" ? "whitespace-nowrap" : undefined}>
            {data.label}
          </span>
        ) : null}
      </div>
    </SidebarLink>
  );
}
